import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import axios from "axios";
import { API_BASE } from "../../../config";

import { motion } from "framer-motion";

import {
  Send,
  Paperclip,
  ShieldCheck,
  User,
  CheckCircle2,
} from "lucide-react";

const statuses = [
  "Open",
  "In Progress",
  "Resolved",
  "Closed",
];

const statusColors = {
  Open:
    "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300",

  "In Progress":
    "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300",

  Resolved:
    "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300",

  Closed:
    "bg-gray-200 text-gray-700 dark:bg-gray-500/20 dark:text-gray-300",
};

const AdminTicketThread = ({
  selectedTicket,
  refreshTickets,
}) => {

  const [messages, setMessages] =
    useState([]);
    const [

lastMessageCount,

setLastMessageCount

] =
useState(0);

  const [loading, setLoading] =
    useState(false);

  const [reply, setReply] =
    useState("");

  const [sending, setSending] =
    useState(false);

  const [attachment, setAttachment] =
    useState(null);

  const [statusLoading,
    setStatusLoading] =
    useState(false);

  const [currentStatus,
    setCurrentStatus] =
    useState(
      selectedTicket?.status || "Open"
    );

  const messagesEndRef =
    useRef(null);

  const admin =
    JSON.parse(localStorage.getItem("user"));

  const token = admin?.token;

  // ====================================
  // AUTO SCROLL
  // ====================================
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  };

  // ====================================
  // FETCH MESSAGES
  // ====================================
  const fetchMessages = async () => {

    if (!selectedTicket?._id) return;

    try {
      setLoading(true);

      const res =
await axios.get(

`${API_BASE}/api/admin/feedback/tickets/${selectedTicket._id}/messages`,

{

headers:{

Authorization:
`Bearer ${token}`,

},

}

);

const incoming =

Array.isArray(
res.data?.messages
)

?

res.data.messages

:

[];

if (

incoming.length !==

lastMessageCount

){

setMessages(
incoming
);

setLastMessageCount(
incoming.length
);

}

setCurrentStatus(

res.data?.ticket?.status ||

"Open"

);    

    } catch (error) {
      console.error(
        "Admin Fetch Messages Error:",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  // ====================================
  // INITIAL FETCH
  // ====================================
  useEffect(() => {

if (
selectedTicket?._id
){

fetchMessages();

}

}, [

selectedTicket?._id

]);

  // ====================================
  // AUTO SCROLL
  // ====================================
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // ====================================
  // SEND REPLY
  // ====================================
  const sendReply = async () => {

    if (!reply.trim() && !attachment)
      return;

    try {
      setSending(true);

      const formData = new FormData();

      formData.append(
        "message",
        reply
      );

      if (attachment) {
        formData.append(
          "attachment",
          attachment
        );
      }

      await axios.post(
`${API_BASE}/api/admin/feedback/tickets/${selectedTicket._id}/reply`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type":
              "multipart/form-data",
          },
        }
      );

      setReply("");
      setAttachment(null);

      fetchMessages();

setTimeout(
refreshTickets,
300
);

    } catch (error) {
      console.error(
        "Admin Reply Error:",
        error
      );
    } finally {
      setSending(false);
    }
  };

  // ====================================
  // UPDATE STATUS
  // ====================================
  const updateStatus = async (
    newStatus
  ) => {

    try {
      setStatusLoading(true);

      await axios.put(
`${API_BASE}/api/admin/feedback/tickets/${selectedTicket._id}/status`,
        {
          status: newStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setCurrentStatus(newStatus);

      setTimeout(
refreshTickets,
300
);

    } catch (error) {
      console.error(
        "Update Status Error:",
        error
      );
    } finally {
      setStatusLoading(false);
    }
  };

  // ====================================
  // EMPTY STATE
  // ====================================
  if (!selectedTicket) {
    return (
      <div
        className="
          h-full
          flex
          items-center
          justify-center
          text-center
          p-6
        "
      >
        <div>

          <div
            className="
              w-24
              h-24
              rounded-full
              bg-gradient-to-r
              from-blue-500/20
              to-purple-500/20
              flex
              items-center
              justify-center
              mx-auto
              mb-5
            "
          >
            <ShieldCheck
              size={42}
              className="
                text-blue-400
              "
            />
          </div>

          <h2
            className="
              text-2xl
              font-bold
              text-gray-800
              dark:text-white
            "
          >
            Support Inbox
          </h2>

          <p
            className="
              text-gray-500
              mt-2
            "
          >
            Select a ticket to
            manage conversation.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">

      {/* HEADER */}
      <div
        className="
          p-5
          border-b
          border-gray-200
          dark:border-gray-700
          bg-gray-50
          dark:bg-gray-900
        "
      >

        <div
          className="
            flex
            items-start
            justify-between
            gap-5
          "
        >

          {/* LEFT */}
          <div>

            <h2
              className="
                text-2xl
                font-bold
                text-gray-800
                dark:text-white
              "
            >
              {selectedTicket.subject}
            </h2>

            <div
              className="
                mt-2
                flex
                items-center
                gap-2
                flex-wrap
              "
            >

              <span
                className="
                  px-3
                  py-1
                  rounded-full
                  text-xs
                  bg-blue-500/10
                  text-blue-400
                "
              >
                {selectedTicket.category}
              </span>

              <span
                className="
                  px-3
                  py-1
                  rounded-full
                  text-xs
                  bg-purple-500/10
                  text-purple-400
                "
              >
                {selectedTicket.priority}
              </span>

              <span
                className={`
                  px-3
                  py-1
                  rounded-full
                  text-xs
                  font-medium
                  ${statusColors[
                    currentStatus
                  ]}
                `}
              >
                {currentStatus}
              </span>
            </div>

            {/* USER */}
            <div
              className="
                mt-4
                flex
                items-center
                gap-3
              "
            >

              <div
                className="
                  w-10
                  h-10
                  rounded-full
                  bg-gradient-to-r
                  from-blue-500
                  to-purple-500
                  flex
                  items-center
                  justify-center
                  text-white
                "
              >
                <User size={18} />
              </div>

              <div>
                <h3
                  className="
                    font-semibold
                    text-gray-800
                    dark:text-white
                  "
                >
                  {
                    selectedTicket?.user
                      ?.name
                  }
                </h3>

                <p
                  className="
                    text-sm
                    text-gray-500
                  "
                >
                  {
                    selectedTicket?.user
                      ?.email
                  }
                </p>
              </div>
            </div>
          </div>

          {/* STATUS SELECT */}
          <div className="min-w-[200px]">

            <label
              className="
                block
                text-sm
                font-medium
                text-gray-500
                mb-2
              "
            >
              Update Status
            </label>

            <select
              value={currentStatus}
              disabled={statusLoading}
              onChange={(e) =>
                updateStatus(
                  e.target.value
                )
              }
              className="
                w-full
                rounded-2xl
                border
                border-gray-300
                dark:border-gray-700
                bg-white
                dark:bg-gray-800
                px-4
                py-3
                text-gray-800
                dark:text-white
                outline-none
              "
            >
              {statuses.map((status) => (
                <option
                  key={status}
                  value={status}
                >
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* MESSAGES */}
      <div
        className="
          flex-1
          overflow-y-auto
          px-5
          py-6
          space-y-5
          bg-gradient-to-b
          from-transparent
          to-black/[0.02]
          dark:to-white/[0.02]
        "
      >

        {/* LOADING */}
        {loading && (
          <div className="space-y-4">

            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="
                  h-24
                  rounded-2xl
                  bg-gray-200
                  dark:bg-gray-800
                  animate-pulse
                "
              />
            ))}
          </div>
        )}

        {/* MESSAGES */}
        {!loading &&
          messages.map((msg, index) => {

            const isAdmin =
              msg.senderType ===
              "admin";

            return (
              <motion.div
                key={msg._id}

                initial={{
                  opacity: 0,
                  y: 10,
                }}

                animate={{
                  opacity: 1,
                  y: 0,
                }}

                transition={{
                  delay: index * 0.03,
                }}

                className={`
                  flex
                  ${
                    isAdmin
                      ? "justify-end"
                      : "justify-start"
                  }
                `}
              >

                <div
                  className={`
                    max-w-[75%]
                    rounded-3xl
                    px-5
                    py-4
                    shadow-lg
                    border

                    ${
                      isAdmin
                        ? `
                          bg-gradient-to-r
                          from-blue-600
                          to-purple-600
                          text-white
                          border-blue-400/30
                        `
                        : `
                          bg-white
                          dark:bg-gray-800
                          border-gray-200
                          dark:border-gray-700
                          text-gray-800
                          dark:text-white
                        `
                    }
                  `}
                >

                  {/* SENDER */}
                  <div
                    className="
                      flex
                      items-center
                      gap-2
                      mb-2
                    "
                  >

                    {isAdmin ? (
                      <ShieldCheck
                        size={15}
                      />
                    ) : (
                      <User size={15} />
                    )}

                    <span
                      className="
                        text-xs
                        font-semibold
                        opacity-80
                      "
                    >
                      {isAdmin
                        ? "Admin"
                        : "User"}
                    </span>
                  </div>

                  {/* MESSAGE */}
                  <p
                    className="
                      whitespace-pre-wrap
                      break-words
                      text-sm
                      leading-relaxed
                    "
                  >
                    {msg.message}
                  </p>

                  {/* ATTACHMENT */}
                  {msg.attachment && (
                    <a
                      href={msg.attachment}
                      target="_blank"
                      rel="noreferrer"
                      className="
                        mt-3
                        inline-flex
                        items-center
                        gap-2
                        underline
                        text-sm
                      "
                    >
                      <Paperclip
                        size={14}
                      />
                      View Attachment
                    </a>
                  )}

                  {/* TIME */}
                  <div
                    className="
                      mt-3
                      text-[11px]
                      opacity-60
                      text-right
                    "
                  >
                    {new Date(
                      msg.createdAt
                    ).toLocaleString()}
                  </div>
                </div>
              </motion.div>
            );
          })}

        <div ref={messagesEndRef} />
      </div>

      {/* REPLY BOX */}
      {!(
        currentStatus ===
        "Closed"
      ) && (

        <div
          className="
            p-4
            border-t
            border-gray-200
            dark:border-gray-700
            bg-white
            dark:bg-gray-900
          "
        >

          {/* FILE */}
          {attachment && (
            <div
              className="
                mb-3
                text-sm
                text-blue-500
                flex
                items-center
                gap-2
              "
            >
              <Paperclip
                size={14}
              />

              {attachment.name}
            </div>
          )}

          <div className="flex gap-3">

            {/* TEXTAREA */}
            <textarea
              rows={2}
              value={reply}
              onChange={(e) =>
                setReply(
                  e.target.value
                )
              }
              placeholder="Type your reply..."
              className="
                flex-1
                rounded-2xl
                border
                border-gray-300
                dark:border-gray-700
                bg-white
                dark:bg-gray-800
                px-4
                py-3
                text-gray-800
                dark:text-white
                resize-none
                outline-none
              "
            />

            {/* ATTACHMENT */}
            <label
              className="
                w-12
                h-12
                rounded-2xl
                border
                border-gray-300
                dark:border-gray-700
                flex
                items-center
                justify-center
                cursor-pointer
                hover:bg-gray-100
                dark:hover:bg-gray-800
                transition
              "
            >
              <Paperclip
                size={18}
              />

              <input
                type="file"
                hidden
                onChange={(e) =>
                  setAttachment(
                    e.target.files[0]
                  )
                }
              />
            </label>

            {/* SEND */}
            <button
              onClick={sendReply}
              disabled={sending}
              className="
                w-12
                h-12
                rounded-2xl
                bg-gradient-to-r
                from-blue-600
                to-purple-600
                text-white
                flex
                items-center
                justify-center
                hover:scale-105
                transition
                disabled:opacity-50
              "
            >
              {sending ? (
                <CheckCircle2
                  size={18}
                />
              ) : (
                <Send size={18} />
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTicketThread;